import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';

import userService from '../../../services/userService';
import authService from '../../../services/authService';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as userSlice from '../../../data/user.slice';

import {
  selectEmailVerificationSuccess,
  setEmailVerificationSuccess,
} from '../../../data/user.slice';
import { setSuccessMessage } from '../../../data/ui.slice';

import PasswordRequirements from './PasswordRequirements';

const PREFIX = 'CreateAdminAccountForm';

const classes = {
  container: `${PREFIX}-container`,
  form: `${PREFIX}-form`,
  formBody: `${PREFIX}-formBody`,
  formInlineFields: `${PREFIX}-formInlineFields`,
  title: `${PREFIX}-title`,
  linkText: `${PREFIX}-linkText`,
  submit: `${PREFIX}-submit`,
  dividerWrapper: `${PREFIX}-dividerWrapper`,
  dividerLine: `${PREFIX}-dividerLine`,
  dividerText: `${PREFIX}-dividerText`,
  passwordWrapper: `${PREFIX}-passwordWrapper`,
  inlineError: `${PREFIX}-inlineError`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    backgroundColor: theme.palette.secondary.main,
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[5],
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  [`& .${classes.form}`]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
  },
  [`& .${classes.formBody}`]: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(2),
  },
  [`& .${classes.formInlineFields}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    width: '100%',
  },
  [`& .${classes.title}`]: {
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(2.5),
    width: '100%',
  },
  [`& .${classes.linkText}`]: {
    textTransform: 'none',
    justifyContent: 'center',
    paddingLeft: 0,
    paddingRight: 0,
    fontWeight: 400,
    color: theme.palette.buttonColor.main,
    textDecoration: 'none',
    '&:hover': {
      cursor: 'pointer',
      color: theme.palette.buttonHoverColor.main,
      textDecoration: 'underline',
      backgroundColor: theme.palette.background.default,
    },
  },
  [`& .${classes.submit}`]: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
  [`& .${classes.dividerWrapper}`]: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(2),
  },
  [`& .${classes.dividerLine}`]: {
    flex: 1,
    height: '1px',
    backgroundColor: theme.palette.divider,
  },
  [`& .${classes.dividerText}`]: {
    fontWeight: 600,
  },
  [`& .${classes.inlineError}`]: {
    marginTop: theme.spacing(-2),
    paddingLeft: theme.spacing(0.5),
    fontSize: 14,
  },
}));

interface CreateAdminAccountFormProps {
  verificationToken: string | null;
}

export default function CreateAdminAccountForm(
  props: CreateAdminAccountFormProps,
) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginButton, setShowLoginButton] = useState(false);
  const [userFirstName, setUserFirstName] = useState<string | null>(null);
  const [userLastName, setUserLastName] = useState<string | null>(null);
  const [emailAddress, setEmailAddress] = useState<string | null>(null);
  const { register, handleSubmit, formState, watch } = useForm();
  const password = watch('password');

  const loggedInUser = useAppSelector(userSlice.selectLoggedInUserInfo);
  const emailVerificationSuccess = useAppSelector(
    selectEmailVerificationSuccess,
  );

  const passwordChecks = {
    lowercase: /[a-z]/,
    uppercase: /[A-Z]/,
    number: /[0-9]/,
    special: /[!@#$%.]/,
    minLength: 8,
  };

  const [createAccountError, setCreateAccountError] =
    React.useState<string>('');

  const onSubmit = (data: any) => {
    userService
      .clientAdminSetPassword(
        props.verificationToken || '',
        data.confirmPassword,
        data.firstName,
        data.lastName,
      )
      .subscribe({
        next: (response: any) => {
          if (response && emailAddress) {
            authService
              .memberLogin(emailAddress, data.confirmPassword)
              .then(() => {
                dispatch(userSlice.setLoggedInUserInitialized(false));
                dispatch(userSlice.fetchLoggedInUserInfoAsync());
              });
          } else {
            setCreateAccountError(t('member.failedToSetPasswordError'));
          }
        },
        error: (err) => {
          if (err.message) {
            setCreateAccountError(String(err.message) || '');
          } else {
            setCreateAccountError(String(err) || '');
          }
        },
      });
  };

  React.useEffect(() => {
    if (props.verificationToken) {
      userService.clientAdminVerifyToken(props.verificationToken).subscribe({
        next: (response: any) => {
          if (response) {
            if (response.isActive) {
              setUserFirstName(response.userFirstName);
              setUserLastName(response.userLastName);
              setEmailAddress(response.emailAddress);
              setCreateAccountError('');
              dispatch(setEmailVerificationSuccess(true));
            } else {
              setCreateAccountError(t('member.verificationEmailExpired'));
              setShowLoginButton(true);
            }
          }
        },
        error: (err) => {
          if (err.validationErrors) {
            let issues = '';
            if (err.validationErrors.issues) {
              err.validationErrors.issues.forEach((issue: any) => {
                issues += `${issue.code}: ${issue.message}\n`;
              });
            }
            setCreateAccountError(issues || '');
          } else if (err.message) {
            setCreateAccountError(String(err.message) || '');
          } else if (err.isActive === false) {
            setCreateAccountError(t('member.verificationEmailExpired'));
            setShowLoginButton(true);
          } else {
            navigate('/');
          }
        },
      });
    }
  }, [props.verificationToken]);

  useEffect(() => {
    if (loggedInUser) {
      navigate('/');
    }
  }, [loggedInUser]);

  useEffect(() => {
    if (emailVerificationSuccess) {
      dispatch(setSuccessMessage(t('member.emailVerified')));
      dispatch(setEmailVerificationSuccess(false));
    }
  }, [emailVerificationSuccess]);

  return (
    <Root className={classes.container}>
      <Box className={classes.form}>
        <Typography variant="h3" component="h1" className={classes.title}>
          {t('member.createAccount')}
        </Typography>
        {(emailAddress || createAccountError) && (
          <form onSubmit={handleSubmit(onSubmit)} className={classes.formBody}>
            <Box className={classes.formInlineFields}>
              <TextField
                fullWidth
                label={t('client.firstName')}
                variant="outlined"
                required
                {...register('firstName', { required: true })}
                defaultValue={userFirstName || ''}
              />

              <TextField
                fullWidth
                label={t('client.lastName')}
                variant="outlined"
                required
                {...register('lastName', { required: true })}
                defaultValue={userLastName || ''}
              />
            </Box>
            <TextField
              fullWidth
              label={t('client.email')}
              variant="outlined"
              {...register('email', {
                required: true,
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: t('member.invalidEmailAddress'),
                },
              })}
              defaultValue={emailAddress || ''}
              InputProps={{
                readOnly: true,
              }}
            />
            {formState.errors['email'] && (
              <FormHelperText error className={classes.inlineError}>
                {String(formState.errors['email'].message || '')}
              </FormHelperText>
            )}
            <Box className={classes.passwordWrapper}>
              <TextField
                fullWidth
                label={t('users.password')}
                variant="outlined"
                required
                type={showPassword ? 'text' : 'password'}
                value={password}
                {...register('password', {
                  required: true,
                  validate: (value) =>
                    (value.length >= passwordChecks.minLength &&
                      passwordChecks.lowercase.test(value) &&
                      passwordChecks.number.test(value) &&
                      passwordChecks.special.test(value) &&
                      passwordChecks.uppercase.test(value)) ||
                    t('member.invalidPassword'),
                })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={t('login.togglePassword')}>
                        {showPassword ? (
                          <VisibilityOffOutlined />
                        ) : (
                          <VisibilityOutlined />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {!!password && <PasswordRequirements password={password} />}
            </Box>

            <TextField
              fullWidth
              label={t('member.confirmPassword')}
              variant="outlined"
              required
              type={showConfirmPassword ? 'text' : 'password'}
              {...register('confirmPassword', {
                required: true,
                validate: (value) =>
                  password === value || t('member.passwordMismatch'),
              })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label={t('login.toggleConfirmPassword')}
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }>
                      {showConfirmPassword ? (
                        <VisibilityOffOutlined />
                      ) : (
                        <VisibilityOutlined />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {formState.errors['confirmPassword'] && (
              <FormHelperText error className={classes.inlineError}>
                {String(formState.errors['confirmPassword'].message || '')}
              </FormHelperText>
            )}

            {createAccountError && (
              <Typography variant="body2" color="error">
                {createAccountError}
              </Typography>
            )}

            <Button
              color="primary"
              variant="contained"
              size="large"
              className={classes.submit}
              type="submit">
              {t('member.createAccount')}
            </Button>

            {showLoginButton && (
              <Button
                color="primary"
                variant="text"
                size="large"
                className={classes.linkText}
                onClick={() => navigate('/clientLogin')}>
                {t('member.backToLogin')}
              </Button>
            )}
          </form>
        )}
      </Box>
    </Root>
  );
}
